<template>
  <template v-if="mode === 'edit'">
    <div
      :class="[
        'py-2 ssn-field',
        field.error ? 'my-2 px-2 rounded border-2 border-brand-red bg-brand-red bg-opacity-10' : ''
      ]"
      :name="field.name"
      :field-id="field.field_id"
      :required="field.required"
    >
      <!-- alert container -->
      <label v-if="field.label" for="input" class="block">
        {{ field.label }}
        <span v-if="field.required" class="text-brand-red">*</span>
      </label>
      <div class="flex flex-row">
        <div class="flex-1">
          <SsnInput :ssn="field.value" :disabled="skipSSN" @update="updateValue" />
        </div>
      </div>
      <template v-if="field.help_text">
        <p class="text-brand-mid-gray italic text-xs">{{ field.help_text }}</p>
      </template>
      <template v-if="field.error">
        <p class="text-brand-red py-1">{{ errorMessage }}</p>
      </template>

      <div class="flex items-center py-1">
        <input
          id="skip-ssn"
          type="checkbox"
          class="rounded border-gray-300 text-brand-green focus:ring-brand-green"
          @change="checkSkipSSN"
          ref="skip-ssn"
        />
        <label for="skip-ssn" class="ml-2 block text-brand-black">I don't have a SSN</label>
      </div>
    </div>

    <TransitionRoot appear :show="showModal" as="template">
      <Dialog as="div" @close="closeModal(false)">
        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="min-h-screen px-4 text-center flex">
            <DialogOverlay class="fixed inset-0 bg-black opacity-50" />

            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <div
                class="inline-block w-full max-w-screen-sm px-6 py-10 m-auto overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded flex flex-col justify-between skip-modal"
              >
                <div class="flex pb-5">
                  <div class="flex items-center mr-2">
                    <ExclamationStopIcon :classes="'w-8 h-8'" />
                  </div>
                  <div class="flex items-center">
                    <h5 class="text-brand-red text-xl">
                      Warning: Not entering your SSN may impact your approval
                    </h5>
                  </div>
                </div>

                <p class="py-1 text-sm">
                  If you do not have an SSN, your reports may not process successfully, thereby
                  impacting your application processing. You'll want to reach out to the property
                  representative to see if they may want additional documentation to assess your
                  creditworthiness once you have submitted your application.
                </p>

                <p class="py-1 text-sm">
                  If you do have an SSN, it is important to enter it here. Your SSN will be fully
                  encrypted for reports processing.
                </p>

                <div class="flex flex-row justify-end pt-10">
                  <div class="flex items-center w-1/4">
                    <PrimaryButton @click="closeModal(false)">Cancel </PrimaryButton>
                  </div>
                  <div class="ml-10 flex items-center w-1/3">
                    <PrimaryButton
                      :classes="'bg-brand-red border-brand-red hover:bg-brand-red'"
                      @click="closeModal(true)"
                      >Proceed without SSN</PrimaryButton
                    >
                  </div>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </template>
  <template v-if="mode === 'view'">
    <div class="flex flex-col md:flex-row justify-between w-full">
      <div class="flex items-center md:w-1/2 py-1 px-1">
        <p>{{ field.label }}</p>
      </div>
      <div class="flex items-center md:w-1/2 py-1 px-1">
        <p class="font-semibold">{{ field.value }}</p>
        <router-link :to="{ name: 'home_applicant_ssn' }" target="_blank">
          <QuestionMarkCircleIcon class="text-brand-blue w-5 h-5 ml-1" />
        </router-link>
      </div>
    </div>
  </template>

  <template v-if="mode === 'form_edit'">
    <div class="flex flex-col w-full">
      <div class="flex w-full relative">
        <SingleLineInput
          :disabled="true"
          placeholder="999-99-9999"
          value="***-**-****"
          :type="'text'"
        ></SingleLineInput>
        <div class="ml-2 flex-3 flex justify-center cursor-pointer absolute right-2 top-5 md:top-6">
          <img src="@/assets/icons/show.svg" />
        </div>
      </div>
      <TextDisplayEditToggle
        :helpText="field.help_text"
        :type="'field_subtext'"
        @input="updateHelpText"
        :disabled="field.editable === false"
      />
    </div>
  </template>
</template>
<script>
import { mapGetters } from "vuex"
import { QuestionMarkCircleIcon } from "@heroicons/vue/outline"

import { TransitionRoot, TransitionChild, Dialog, DialogOverlay } from "@headlessui/vue"

export default {
  components: {
    QuestionMarkCircleIcon,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay
  },
  props: {
    field: Object,
    mode: {
      type: String
    },
    stage: {
      type: String
    }
  },
  emits: ["update", "skip"],
  mounted() {},
  watch: {
    "field.error": {
      handler(val) {
        if (val && this.getApplicationTemplateError && this.getApplicationTemplateError.response) {
          const errors = this.getApplicationTemplateError.response.data.error
          Object.keys(errors).forEach((key) => {
            if (key === this.field.name) {
              this.errorMessage = errors[key][0]
            }
          })
        }
      }
    }
  },
  computed: {
    ...mapGetters(["getApplicationTemplateError"]),
    validSSN() {
      if (!this.field.value) {
        return false
      }
      return (
        this.field.value.split("-")[0].length === 3 &&
        this.field.value.split("-")[1].length === 2 &&
        this.field.value.split("-")[2].length === 4
      )
    },
    isValid() {
      if (!this.field.value && this.field.required) return false

      if (this.field.value && !this.validSSN) return false

      return true
    }
  },
  data() {
    return {
      show: false,
      errorMessage: "",
      entered: false,
      skipSSN: false,
      showModal: false
    }
  },
  methods: {
    updateHelpText(ev) {
      // eslint-disable-next-line vue/no-mutating-props
      this.field.help_text = ev.target.value
    },
    updateValue(val) {
      this.$emit("update", val)
    },
    validateField() {
      if (!this.isValid) {
        if (this.field.required && !this.field.value) {
          this.errorMessage = "This field is required."
        } else {
          this.errorMessage = "Please enter a valid SSN."
        }
      } else {
        this.errorMessage = ""
      }
    },
    checkSkipSSN() {
      if (!this.skipSSN) {
        this.openModal()
      } else {
        this.skipSSN = false
        this.$emit("skip", false)
        this.$emit("update", null)
      }
    },
    openModal() {
      this.showModal = true
    },
    closeModal(skip) {
      this.showModal = false
      this.skipSSN = skip

      this.$refs["skip-ssn"].checked = this.skipSSN

      if (skip) {
        this.$emit("skip", true)
        this.$emit("update", "999-99-9999")
      } else {
        this.$emit("skip", false)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
// @font-face {
//   font-family: "asterisk";
//   src: url("./../../assets/fonts/asterisk-mask.eot?0000");
//   src: url("./../../assets/fonts/asterisk-mask.eot?0000#iefix")
//       format("embedded-opentype"),
//     url("./../../assets/fonts/asterisk-mask.woff2?0000") format("woff2"),
//     url("./../../assets/fonts/asterisk-mask.woff?0000") format("woff"),
//     url("./../../assets/fonts/asterisk-mask.ttf?0000") format("truetype"),
//     url("./../../assets/fonts/asterisk-mask.svg?0000#asterisk") format("svg");
//   font-weight: normal;
//   font-style: normal;
//   unicode-range: U+2160 to U+2188;
// }

// input[type="password"] {
//   @apply text-sm;
//   font-family: "asterisk";
//   font-style: normal;
//   font-weight: normal;
//   speak: none;

//   /* For safety - reset parent styles, that can break glyph codes*/
//   font-variant: normal;
//   text-transform: none;

//   /* Font smoothing. That was taken from TWBS */
//   -webkit-font-smoothing: antialiased;
//   -moz-osx-font-smoothing: grayscale;
//   /* Uncomment for 3D effect */
//   /* text-shadow: 1px 1px 1px rgba(127, 127, 127, 0.3); */

//   /* add spacing to better separate each image */
//   letter-spacing: 2px;
// }
</style>
